import type { SoilRound } from '../../services/nutritionService';
import type { EditableRound } from './nutritionProgrammePayload';
import { plannedStartInputValue } from './nutritionProgrammePayload';

/**
 * Pure module behind the round-editor page (Task 8). No React, no network,
 * no i18n — every derivation here is a plain data transform so it can be
 * unit-tested without a DOM.
 *
 * `plannedStartInputValue` / `plannedStartToIso` are NOT defined here: the
 * date-input helper already lives in `nutritionProgrammePayload.ts` (imported
 * above) with its own tests, and the ISO-emission direction happens inside
 * `buildRound` in that same file. Duplicating either would recreate the
 * duplicated-helper defect this project has already been bitten by twice.
 * `buildRoundPayload` is likewise dropped as a pointless alias — Task 9's
 * page imports `buildRound` directly.
 */

/**
 * Maps a persisted round into the editor's string-based working shape.
 * Numeric fields use `== null` (not `||`) so a legitimate `0` (e.g. a
 * waterRatePerHa of 0) hydrates to `"0"`, not `""`.
 */
export function hydrateRound(round: SoilRound): EditableRound {
  return {
    roundId: round.roundId,
    type: round.type,
    name: round.name,
    month: round.month == null ? '' : String(round.month),
    plannedStart: plannedStartInputValue(round.plannedStart),
    waterRatePerHa: round.waterRatePerHa == null ? '' : String(round.waterRatePerHa),
    groups: (round.groups ?? []).map((g) => ({
      groupId: g.groupId,
      label: g.label ?? '',
      orchardIds: g.orchardIds,
      products: g.products.map((p) => ({
        productId: p.productId,
        productName: p.productName,
        unit: p.unit,
        doseBasis: p.doseBasis ?? 'perTree',
        value: String(p.value),
      })),
    })),
  };
}

export interface RoundEditorErrors {
  waterRateRequired: boolean;
  /**
   * #537 (fix round 2): mirrors the server's `plannedStartMissingInRound`
   * predicate — gated on the SAME condition (products AND orchards both
   * present) so this cannot diverge from it. Without a client-side rule the
   * round editor's own Save produced an unactionable "Could not save this
   * round." 400 for every one of the 25 undated legacy rounds it opened —
   * the norm case, not an edge, on live data.
   */
  plannedStartRequired: boolean;
  missingUnitLines: number;
  /**
   * Display labels for the lines counted by `missingUnitLines`, in round
   * order. A bare count cannot be rendered as an actionable message — the
   * offending line may well sit in a group tab that is not the open one — so
   * the page names the products instead. `productName` when the catalog
   * supplied one, else the raw `productId`, so a line never renders as blank.
   */
  missingUnitProducts: string[];
  nameRequired: boolean;
}

/**
 * Validation rules for a single round, mirroring the whole-form checks that
 * used to live inline in NutritionProgrammeFormPage: a water rate is only
 * required once the round carries at least one product (in any group), and a
 * product line only counts as "missing a unit" once a product has actually
 * been chosen on it — an empty, not-yet-configured line is not an error.
 */
export function roundEditorErrors(round: EditableRound): RoundEditorErrors {
  const nameRequired = round.name.trim() === '';
  const hasProducts = round.groups.some((g) => g.products.length > 0);
  const hasOrchards = round.groups.some((g) => g.orchardIds.length > 0);
  const waterRateRequired = hasProducts && round.waterRatePerHa.trim() === '';
  // #537: required once the round is CONFIGURED — products AND orchards both
  // present — same paired condition as the server rule this mirrors. A bare
  // or partially-built round is not yet a problem, matching waterRateRequired
  // above.
  const plannedStartRequired = hasProducts && hasOrchards && round.plannedStart.trim() === '';
  const missingUnitProducts = round.groups.reduce<string[]>(
    (names, g) =>
      names.concat(
        g.products
          .filter((p) => p.productId.trim() !== '' && (p.unit == null || p.unit.trim() === ''))
          .map((p) => (p.productName || '').trim() || p.productId.trim()),
      ),
    [],
  );
  return {
    waterRateRequired,
    plannedStartRequired,
    missingUnitLines: missingUnitProducts.length,
    missingUnitProducts,
    nameRequired,
  };
}

export function canSaveRound(round: EditableRound): boolean {
  const errors = roundEditorErrors(round);
  return !errors.nameRequired && !errors.waterRateRequired && !errors.plannedStartRequired
    && errors.missingUnitLines === 0;
}

/**
 * Orchards still pickable by `groupId` = every orchard NOT held by ANOTHER
 * group of the SAME round (mutual exclusivity is per-round, since groups are
 * round-owned). Mirrors NutritionProgrammeFormPage's `availableOrchards`
 * exactly, but takes the orchard list as a parameter so this module stays
 * network-free. `Array.from` is not needed here — the Set is only queried
 * with `.has`, never spread — but per GC 13, a Set must never be spread
 * (`[...set]`) under this project's ES5 build target.
 */
export function availableOrchards(
  round: EditableRound,
  groupId: string,
  allOrchards: string[],
): string[] {
  const taken = new Set(
    round.groups.filter((g) => g.groupId !== groupId).flatMap((g) => g.orchardIds),
  );
  return allOrchards.filter((code) => !taken.has(code));
}

/**
 * Derives the next `g<N>` id from the round's OWN groups (max numeric suffix
 * + 1) instead of a module-level counter. The old editor's `groupSeq` was
 * global and needed a hydrate-time reseed to the highest existing suffix
 * across ALL rounds (NutritionProgrammeFormPage.tsx:188-197), with a whole
 * test file (`idCollision`) existing only to pin that reseed. A per-round
 * derivation is stateless and cannot collide, so the bug class disappears
 * rather than moving. A non-numeric suffix (or an id not starting with "g")
 * is ignored rather than propagated into `gNaN`. Round ids are NOT minted
 * here — the round editor never creates a round.
 */
export function nextGroupId(round: EditableRound): string {
  const maxSuffix = round.groups.reduce((max, g) => {
    const n = g.groupId.startsWith('g') ? Number(g.groupId.slice(1)) : NaN;
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0);
  return `g${maxSuffix + 1}`;
}
